import { Box, Link, Typography } from '@mui/material'
import {
  DataGrid,
  type DataGridProps,
  type GridColumns,
  GridOverlay,
  type GridRowParams
} from '@mui/x-data-grid'
import React, { useCallback, useState } from 'react'
import { useBoolean } from 'react-use'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { AgentDetailsDrawer } from 'components/configurations/agents/AgentDetailsDrawer'
import { DeactivateAgentButton } from 'components/configurations/agents/DeactivateAgentButton'
import { ReactivateAgentButton } from 'components/configurations/agents/ReactivateAgentButton'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { muiTheme } from 'mui-theme'
import { type Agent, AgentStatus, AgentType } from 'types/api/agents/agents'
import {
  clientsIdActivatePut,
  clientsIdDelete,
  failureFlagsAppsProtectPost,
  failureFlagsAppsUnprotectPost
} from 'utils/endpoints-access'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles,
  safeRowClick
} from 'utils/mui-components/data-grid-utils'

function getAgentStatusColor(status: AgentStatus, isWarning: boolean) {
  if (status === AgentStatus.ACTIVE && isWarning) {
    return 'warning.main'
  }

  if (status === AgentStatus.ACTIVE) {
    return 'success.main'
  }

  return 'text.primary.main'
}

const AgentListEmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No agents found</Typography>
    </GridOverlay>
  )
}

export const AgentsList: React.FC<{ agents: Agent[] }> = ({ agents }) => {
  const [pageSize, setPageSize] = useState(15)
  const [isOpen, setIsOpen] = useBoolean(false)
  const [activeAgent, setActiveAgent] = useState<Agent | null>(null)

  const { hasEndpointAccessForAll } = useAuth()

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
  }

  const handleRowClick: DataGridProps['onRowClick'] = (
    params: GridRowParams<Agent>,
    event
  ) => {
    safeRowClick(event, () => {
      setActiveAgent(params.row)
      setIsOpen(true)
    })
  }

  const handleOpen = (agent: Agent) => {
    setActiveAgent(agent)
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setActiveAgent(null)
  }

  const hasPrivilegesToEdit = useCallback(
    (agent: Agent) => {
      const isInfrastructureAgent = agent.type === AgentType.INFRASTRUCTURE

      if (isInfrastructureAgent) {
        return hasEndpointAccessForAll([clientsIdActivatePut, clientsIdDelete])
      } else {
        return hasEndpointAccessForAll([
          failureFlagsAppsProtectPost,
          failureFlagsAppsUnprotectPost
        ])
      }
    },
    [hasEndpointAccessForAll]
  )

  const columns: GridColumns<Agent> = [
    {
      ...defaultColumnProps,
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) => {
        const { status, warningCount } = row

        const isWarning = status === AgentStatus.ACTIVE && warningCount > 0

        return (
          <Typography
            color={getAgentStatusColor(status, isWarning)}
            variant="inherit"
          >
            {status}
            {isWarning && (
              <Icon
                css={{ marginLeft: muiTheme.spacing(0.5) }}
                data-testid="agents-page-list-row-warning-status-icon"
                icon="solidWarning"
              />
            )}
          </Typography>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'id',
      headerName: 'Identifier',
      flex: 3.5,
      renderCell: ({ row }) => {
        return (
          <Link
            data-testid={`agent-list-row-identifier-${row.id}`}
            onClick={() => handleOpen(row)}
          >
            <TruncatedTextWithTooltip
              tooltipProps={{ title: row.id }}
              variant="inherit"
            >
              {row.id}
            </TruncatedTextWithTooltip>
          </Link>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'type',
      headerName: 'Agent Type',
      flex: 1.5,
      valueFormatter: ({ value }) =>
        value === AgentType.INFRASTRUCTURE ? 'Infrastructure' : 'Failure Flag'
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: ({ row }) => {
        if (!hasPrivilegesToEdit(row)) {
          return null
        }

        if (row.status === AgentStatus.ACTIVE) {
          return <DeactivateAgentButton agent={row} />
        }

        if (row.status === AgentStatus.DEACTIVATED) {
          return <ReactivateAgentButton agent={row} />
        }

        // Idle
        return null
      }
    }
  ]

  return (
    <Box data-testid="agents-page-list">
      <AgentDetailsDrawer
        activeAgent={activeAgent}
        isOpen={isOpen}
        onClose={handleClose}
      />
      <DataGrid
        {...defaultGridProps}
        columns={columns}
        components={{
          NoRowsOverlay: AgentListEmptyState
        }}
        getRowId={(row) => row.id}
        pageSize={pageSize}
        rows={agents}
        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
        sx={getGridStyles()}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={handleRowClick}
      />
    </Box>
  )
}
